// src/utils/api.ts - API client for Express backend

import { getAttributionPayload } from './attribution';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5089';

/* ---------------- Auth token attachment (D-002) ---------------- */
// apiFetch is the single choke point that attaches the caller's Clerk session
// token as `Authorization: Bearer <token>`. Because apiFetch is a plain module
// function (not a React hook) it cannot call useAuth(); a small React bridge
// mounted inside <ClerkProvider> (see AuthSync) registers a token getter here
// via registerTokenGetter(). When no getter is registered we fall back to the
// global Clerk instance. Anonymous/public callers resolve to no token and send
// no Authorization header, so public pages keep working. Phase 1: the server
// runs AUTH_ENFORCE=off/log, so a token it ignores is harmless.

export type TokenGetter = () => Promise<string | null>;

let _getToken: TokenGetter | null = null;

// Register (or, with null, unregister) the Clerk token getter. Called by the
// React auth bridge once it has Clerk context.
export function registerTokenGetter(fn: TokenGetter | null): void {
  _getToken = fn;
}

// Resolve the Authorization header for the current caller, or {} when there is
// no signed-in session. Never throws — any failure degrades to no header so a
// transient token error can't break a request (public requests especially).
export async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    const token = _getToken
      ? await _getToken()
      : await (window as any)?.Clerk?.session?.getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

// Helper function for making API calls. Attaches the Clerk bearer token when a
// session exists (see getAuthHeader); anonymous callers send no auth header.
async function apiFetch(path: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${path}`;

  const res = await fetch(url, {
    ...options,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(await getAuthHeader()),
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    let errorBody = '';
    try {
      errorBody = await res.text();
    } catch (e) {
      errorBody = 'Unable to read error response';
    }
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${errorBody}`);
  }

  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;

  const text = await res.text();
  const result = text ? JSON.parse(text) : null;
  return result;
}

/* ---------------- Users API ---------------- */

export interface User {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  shippingAddress?: string;
  bio?: string;
  company?: string;
  subscription?: string;
  subscriptionExpires?: string;
  hasPhysicalCard?: boolean;
  physicalCardCount?: number;
  notifyCardViews?: boolean;
  notifyWeeklyDigest?: boolean;
  notifyMarketing?: boolean;
  activeCardId?: string | null;
  /** Industry tag set during the post-signup picker. Drives preset card
   *  content. Null = the user hasn't been through the picker. */
  industry?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserCreate {
  username: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  shippingAddress?: string;
  bio?: string;
  company?: string;
}

// Get all users
export function fetchUsers(): Promise<User[]> {
  return apiFetch('/api/users');
}

// Public-safe user projection for anonymous card rendering.
// GET /api/users/:id returns ONLY these fields — never account PII (email,
// phone, subscription details/expiry, notify flags). `shippingAddress` is
// returned solely when the supplied card opts to expose it (server-side gate).
export interface PublicUser {
  bio: string | null;
  isPro: boolean;
  shippingAddress?: string;
}

// Get the public-safe projection for a user, scoped to the card being viewed so
// the server can decide whether that card opts into showing the address.
export function fetchPublicUser(id: string, cardId?: string): Promise<PublicUser> {
  const qs = cardId ? `?cardId=${encodeURIComponent(cardId)}` : '';
  return apiFetch(`/api/users/${id}${qs}`);
}

// Get user by email
export function fetchUserByEmail(email: string): Promise<User> {
  return apiFetch(`/api/users/by-email?email=${encodeURIComponent(email)}`);
}

// Create new user (strict create - fails if email exists)
export function createUser(userData: UserCreate): Promise<User> {
  return apiFetch('/api/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

// Sync/Create user (idempotent - recommended for frontend).
// Attaches first-touch attribution so the server can persist it on creation.
// On updates, the server preserves any existing non-null UTM values.
export function syncUser(userData: UserCreate): Promise<User> {
  return apiFetch('/api/users/sync', {
    method: 'POST',
    body: JSON.stringify({ ...userData, attribution: getAttributionPayload() }),
  });
}

// Update user
export function updateUser(
  id: string,
  userData: Partial<UserCreate> & {
    subscription?: string;
    subscriptionExpires?: string | null;
    notifyCardViews?: boolean;
    notifyWeeklyDigest?: boolean;
    notifyMarketing?: boolean;
    activeCardId?: string | number | null;
    industry?: string | null;
  },
): Promise<User> {
  return apiFetch(`/api/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
}

// Delete user
export function deleteUser(id: string): Promise<void> {
  return apiFetch(`/api/users/${id}`, {
    method: 'DELETE',
  });
}

/* ---------------- Cards API ---------------- */

export interface Card {
  id: string;
  userId: string;
  username: string;
  /** Public-facing name visitors see on the card. */
  name: string;
  /** Admin-only label the owner sets to organize their cards
   *  ("YC Demo Day", "Trade show 2026"). Never shown to visitors.
   *  Falls back to a "Card N" display in the editor when null. */
  adminLabel?: string | null;
  position: string;
  email: string;
  phoneNumber?: string;
  websiteUrl?: string;
  company?: string;
  theme?: string;
  wideLogo?: string;
  squareLogo?: string;
  logoStyle?: string;
  showProBadge?: boolean;
  badgeStyle?: string;
  profilePhoto?: string;
  resumeFilename?: string;
  resumeType?: string;
  hasResume?: boolean;
  showEmail?: boolean;
  showPhone?: boolean;
  showContactBox?: boolean;
  showAddress?: boolean;
  vcfIncludeEmail?: boolean;
  vcfIncludePhone?: boolean;
  vcfIncludeAddress?: boolean;
  vcfIncludeWebsite?: boolean;
  usernameChangeCount?: number;
  createdAt: string;
}

export interface CardCreate {
  userId: string;
  /**
   * BL-008: the per-card slug is no longer user-supplied. The server
   * auto-generates `digital_cards.username` as an internal unique key; the
   * public URL is the owner's account handle. Kept optional only for callers
   * that still pass it (ignored server-side).
   */
  username?: string;
  name: string;
  /** Owner-only label, optional. */
  adminLabel?: string | null;
  position: string;
  email: string;
  phoneNumber?: string;
  websiteUrl?: string;
  company?: string;
  theme?: string;
  wideLogo?: string;
  squareLogo?: string;
  logoStyle?: string;
  showProBadge?: boolean;
  badgeStyle?: string;
  profilePhoto?: string;
  resumeData?: string;
  resumeFilename?: string;
  resumeType?: string;
  showEmail?: boolean;
  showPhone?: boolean;
  showContactBox?: boolean;
  showAddress?: boolean;
  vcfIncludeEmail?: boolean;
  vcfIncludePhone?: boolean;
  vcfIncludeAddress?: boolean;
  vcfIncludeWebsite?: boolean;
}

// Get all public cards
export function fetchAllCards(): Promise<Card[]> {
  return apiFetch('/api/cards');
}

// Get all cards, optionally filtered by user
export function fetchCards(userId?: string): Promise<Card[]> {
  const url = userId ? `/api/cards?userId=${userId}` : '/api/cards';
  return apiFetch(url);
}

// Get card by ID
export function fetchCard(id: string): Promise<Card> {
  return apiFetch(`/api/cards/${id}`);
}

// Get card by username
export function fetchCardByUsername(username: string): Promise<Card> {
  return apiFetch(`/api/cards/by-username?username=${encodeURIComponent(username)}`);
}

// D-001: fetch the single curated homepage demo card (the founder's
// "Founder WhoBrew" card), resolved server-side by owner handle + owner-only
// admin_label. Returns the same public display shape as fetchCard. Scoped to
// the homepage PreviewCard demo — not a general per-card lookup.
export function fetchDemoCard(): Promise<Card> {
  return apiFetch('/api/cards/demo-card');
}

// Check for username redirect
export function fetchCardRedirect(username: string): Promise<{ redirectTo: string }> {
  return apiFetch(`/api/cards/redirect?username=${encodeURIComponent(username)}`);
}

// Create card
export function createCard(cardData: CardCreate): Promise<Card> {
  return apiFetch('/api/cards', {
    method: 'POST',
    body: JSON.stringify(cardData),
  });
}

// Update card
export function updateCard(id: string, cardData: Partial<CardCreate>): Promise<Card> {
  return apiFetch(`/api/cards/${id}`, {
    method: 'PUT',
    body: JSON.stringify(cardData),
  });
}

// Delete card
export function deleteCard(id: string): Promise<void> {
  return apiFetch(`/api/cards/${id}`, {
    method: 'DELETE',
  });
}

/* ---------------- Card Requests API ---------------- */

export interface CardRequest {
  id?: string;
  userId?: string;
  name: string;
  websiteUrl: string;
  position1: string;
  position2?: string;
  position3?: string;
  email: string;
  phoneNumber: string;
  extraInfo?: string;
  comments?: string;
  status?: string;
  trackingNumber?: string;
  shippedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type CardRequestCreate = Omit<CardRequest, 'id' | 'userId' | 'createdAt' | 'updatedAt'>;

// Get all card requests, optionally filtered by user
export function fetchCardRequests(userId?: string): Promise<CardRequest[]> {
  const url = userId ? `/api/cardrequests?userId=${userId}` : '/api/cardrequests';
  return apiFetch(url);
}

// Get card request by ID
export function fetchCardRequest(id: string): Promise<CardRequest> {
  return apiFetch(`/api/cardrequests/${id}`);
}

// Create card request. Forwards first-touch attribution so the row records
// which campaign produced the order.
export function createCardRequest(requestData: CardRequestCreate): Promise<CardRequest> {
  return apiFetch('/api/cardrequests', {
    method: 'POST',
    body: JSON.stringify({ ...requestData, attribution: getAttributionPayload() }),
  });
}

// Update card request
export function updateCardRequest(id: string, requestData: Partial<CardRequest>): Promise<CardRequest> {
  return apiFetch(`/api/cardrequests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(requestData),
  });
}

// Delete card request
export function deleteCardRequest(id: string): Promise<void> {
  return apiFetch(`/api/cardrequests/${id}`, {
    method: 'DELETE',
  });
}

// Admin: Get all card requests (no userId filter)
export function fetchAllCardRequests(): Promise<CardRequest[]> {
  return apiFetch('/api/cardrequests');
}

// Admin: Update card request status
export function updateCardRequestStatus(id: string, status: string): Promise<CardRequest> {
  return apiFetch(`/api/cardrequests/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

/* ---------------- Card Links API ---------------- */

export type LinkType = 'url' | 'contact_form';

export type ContactFormFieldType =
  | 'short_text' | 'long_text' | 'email' | 'phone'
  | 'select' | 'multiselect' | 'yes_no'
  /** Resume / file attachment. At most one per form (D-005). */
  | 'file_upload';

export interface ContactFormFieldConfig {
  /** Stable id used as the key in Lead.extraFields. */
  id: string;
  label: string;
  type: ContactFormFieldType;
  required: boolean;
  /** Used for select/multiselect. */
  options?: string[];
}

export interface ContactFormConfig {
  /** The visible CTA on the public card. Defaults to "Contact me". */
  buttonLabel?: string | null;
  /** Optional description shown above the form fields. */
  description?: string | null;
  /** Up to 4 custom fields beyond Name/Phone/Email (Pro only). */
  customFields: ContactFormFieldConfig[];
}

export interface CardLink {
  id: string;
  cardId: string;
  title: string;
  /** Empty for contact_form links. */
  url: string;
  description?: string;
  icon?: string;
  category?: string;
  order: number;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogSiteName?: string;
  customIcon?: string;
  linkType: LinkType;
  /** Present (non-null) for contact_form links. */
  formConfig?: ContactFormConfig | null;
  createdAt: string;
  updatedAt: string;
}

export interface CardLinkCreate {
  cardId: string;
  title: string;
  url?: string;
  description?: string;
  icon?: string;
  category?: string;
  order: number;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogSiteName?: string;
  customIcon?: string;
  linkType?: LinkType;
  formConfig?: ContactFormConfig | null;
}

// Get all card links for a card
export function fetchCardLinks(cardId: string): Promise<CardLink[]> {
  return apiFetch(`/api/cardlinks?cardId=${cardId}`);
}

// Get card link by ID
export function fetchCardLink(id: string): Promise<CardLink> {
  return apiFetch(`/api/cardlinks/${id}`);
}

// Create card link
export function createCardLink(linkData: CardLinkCreate): Promise<CardLink> {
  return apiFetch('/api/cardlinks', {
    method: 'POST',
    body: JSON.stringify(linkData),
  });
}

// Update card link
export function updateCardLink(id: string, linkData: Partial<CardLinkCreate>): Promise<CardLink> {
  return apiFetch(`/api/cardlinks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(linkData),
  });
}

// Delete card link
export function deleteCardLink(id: string): Promise<void> {
  return apiFetch(`/api/cardlinks/${id}`, {
    method: 'DELETE',
  });
}

// Fetch link preview metadata
export function fetchLinkPreview(url: string): Promise<{ title: string | null; description: string | null; image: string | null; siteName: string | null }> {
    return apiFetch(`/api/link-preview?url=${encodeURIComponent(url)}`);
}

// Record a link click
export function recordLinkClick(linkId: string): Promise<void> {
  return apiFetch(`/api/cardlinks/${linkId}/click`, { method: 'POST' }).catch(() => undefined) as Promise<void>;
}

// Get click counts for all links on a card
export function fetchLinkClicks(cardId: string): Promise<Record<string, number>> {
  return apiFetch(`/api/cardlinks/clicks?cardId=${cardId}`).catch(() => ({}));
}

/* ---------------- Card Visits API ---------------- */

export interface CardVisit {
  id: string;
  cardId: string;
  visitorIp?: string;
  userAgent?: string;
  referrer?: string;
  createdAt: string;
}

export interface VisitStats {
  totalVisits: number;
  todayVisits: number;
  weekVisits: number;
  monthVisits: number;
}

// Record a visit to a card
export function recordCardVisit(cardId: string, ref?: string): Promise<any> {
  return apiFetch('/api/cardvisits', {
    method: 'POST',
    body: JSON.stringify({ cardId, ref: ref || undefined }),
  }).catch(() => null);
}

// Get visit timeline data for a card
export function fetchVisitTimeline(cardId: string, period: string): Promise<Array<{date: string; views: number}>> {
    return apiFetch(`/api/cardvisits/timeline?cardId=${cardId}&period=${period}`).catch(() => []);
}

// Get per-link click timeline for a card
export function fetchLinkClickTimeline(cardId: string, period: string): Promise<Array<{linkId: string; linkTitle: string; date: string; clicks: number}>> {
    return apiFetch(`/api/cardlinks/clicks/timeline?cardId=${cardId}&period=${period}`).catch(() => []);
}

// Get visit stats for a card
export function fetchVisitStats(cardId: string): Promise<VisitStats> {
  return apiFetch(`/api/cardvisits/stats?cardId=${cardId}`).catch(() => ({
    totalVisits: 0, todayVisits: 0, weekVisits: 0, monthVisits: 0,
  }));
}

/* ---------------- Notifications API ---------------- */

export interface Notification {
    id: string;
    userId: string;
    title: string;
    message: string;
    type: string;
    isRead: boolean;
    createdAt: string;
}

// Get notifications for a user
export function fetchNotifications(userId: string): Promise<Notification[]> {
    return apiFetch(`/api/notifications?userId=${userId}`).catch(() => []);
}

// Mark a notification as read
export function markNotificationRead(id: string): Promise<void> {
    return apiFetch(`/api/notifications/${id}/read`, { method: 'PATCH' }).catch(() => undefined) as Promise<void>;
}

/* ---------------- Checkout API ---------------- */

export async function createCheckoutSession(priceId: string, email: string, userId?: string): Promise<{ url: string }> {
    return apiFetch('/api/checkout', {
        method: 'POST',
        body: JSON.stringify({ priceId, email, userId, attribution: getAttributionPayload() }),
    });
}

/* ---------------- Billing Portal API ---------------- */

// Create Stripe Billing Portal session
export function createBillingPortalSession(email: string): Promise<{ url: string }> {
    return apiFetch('/api/billing-portal', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

/* ---------------- Resume Helpers ---------------- */

export function getResumeUrl(cardId: string): string {
    const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5089';
    return `${API}/api/cards/${cardId}/resume`;
}

export function getResumeUrlByUsername(username: string): string {
    const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5089';
    return `${API}/api/cards/by-username/${username}/resume`;
}

// Get detailed analytics breakdown for a card
export interface AnalyticsBreakdown {
    label: string;
    count: number;
}
export interface CardAnalytics {
    devices: AnalyticsBreakdown[];
    browsers: AnalyticsBreakdown[];
    operatingSystems: AnalyticsBreakdown[];
    countries: AnalyticsBreakdown[];
    cities: AnalyticsBreakdown[];
    referrers: AnalyticsBreakdown[];
    uniqueVisitors: number;
}
export function fetchCardAnalytics(cardId: string): Promise<CardAnalytics> {
    return apiFetch(`/api/cardvisits/analytics?cardId=${cardId}`);
}

/* ---------------- Leads API ---------------- */

export interface Lead {
    id: string;
    cardId: string;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    jobTitle: string;
    phone: string;
    notes: string;
    status: string;
    source: string;
    emailPermission: string;
    /** Custom contact-form answers, keyed by ContactFormFieldConfig.id. */
    extraFields?: Record<string, string> | null;
    /**
     * Whether this lead carries a visitor-submitted attachment (D-005). The
     * bytes themselves are never in this payload — fetch them from the
     * owner-authenticated endpoint via fetchLeadAttachment().
     */
    hasAttachment?: boolean;
    /** Original filename of that attachment, for display and download. */
    attachmentFilename?: string | null;
    /** Set when the lead came from a contact_form link submission. */
    sourceLinkId?: string | null;
    createdAt: string;
    updatedAt: string;
}

/** Public contact-form submission payload. */
export interface ContactFormSubmission {
  name: string;
  email: string;
  phone: string;
  /** Answers keyed by ContactFormFieldConfig.id for any custom Pro fields. */
  extraFields?: Record<string, string | string[]>;
  /**
   * A file_upload field's attachment (D-005). Sent top-level rather than in
   * extraFields, whose values are capped at 1000 chars server-side. Validated
   * server-side for size and MIME; omitted when nothing was attached.
   */
  attachment?: { data: string; filename: string; type: string } | null;
}

/** Submit a contact-form card link. Public — no auth required. */
export function submitCardLinkForm(linkId: string, data: ContactFormSubmission): Promise<Lead> {
  return apiFetch(`/api/cardlinks/${linkId}/submit`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchLeads(userId: string): Promise<Lead[]> {
    return apiFetch(`/api/leads?userId=${userId}`);
}

/**
 * A user-presentable Error from a failed response.
 *
 * apiFetch bakes the raw response text into its message
 * (`HTTP 503 ...: {"error":...,"missing":[...]}`), which is right for the ~30
 * call sites that only log it but wrong anywhere the message reaches the
 * screen. The wallet endpoints answer with a specific `error` string meant for
 * humans, so surface exactly that and nothing else.
 */
async function cleanApiError(res: Response): Promise<Error> {
  let detail = '';
  try {
    const body = await res.json();
    detail = typeof body?.error === 'string' ? body.error : '';
  } catch {
    // Non-JSON error body; the status line is all we have.
  }
  return new Error(detail || `HTTP ${res.status} ${res.statusText}`);
}

/** Which wallet integrations this server is actually provisioned for (D-007). */
export function fetchWalletCapabilities(): Promise<{ apple: boolean; google: boolean }> {
  return apiFetch('/api/wallet/capabilities');
}

/**
 * An "Add to Google Wallet" save link for the owner's own card (D-007).
 *
 * A bespoke fetch rather than apiFetch, for the error-message reason above:
 * this message is rendered in the dashboard.
 */
export async function fetchGoogleWalletLink(cardId: string): Promise<{ saveUrl: string }> {
  const res = await fetch(`${API_BASE_URL}/api/cards/${cardId}/google-wallet-link`, {
    headers: { Accept: 'application/json', ...(await getAuthHeader()) },
  });
  if (!res.ok) throw await cleanApiError(res);
  return res.json();
}

/**
 * Download the owner's own card as an Apple Wallet .pkpass (D-007).
 *
 * Not routed through apiFetch for the same reason as the lead attachment:
 * apiFetch forces a JSON Content-Type and returns null for a non-JSON body.
 * The route is owner-authenticated, so the token has to travel with the
 * request — a plain <a href> would arrive anonymous and 401.
 */
export async function fetchAppleWalletPass(cardId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}/api/cards/${cardId}/apple-wallet-pass`, {
    headers: { ...(await getAuthHeader()) },
  });
  // The 503 body names the missing configuration; surface it rather than a
  // bare status so an unconfigured server is diagnosable from the UI.
  if (!res.ok) throw await cleanApiError(res);
  return res.blob();
}

/**
 * Download a lead's attachment as a Blob (D-005).
 *
 * Not routed through apiFetch: that sets a JSON Content-Type and returns null
 * for any non-JSON response, which is exactly what this endpoint sends. The
 * route is owner-authenticated, so the token has to travel with the request —
 * a plain <a href> would arrive anonymous and 401.
 */
export async function fetchLeadAttachment(leadId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}/api/leads/${leadId}/attachment`, {
    headers: { ...(await getAuthHeader()) },
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export function createLead(data: Partial<Lead> & { cardId: string; userId: string }): Promise<Lead> {
    return apiFetch('/api/leads', { method: 'POST', body: JSON.stringify(data) });
}

export function updateLead(id: string, data: Partial<Lead>): Promise<Lead> {
    return apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteLead(id: string): Promise<void> {
    return apiFetch(`/api/leads/${id}`, { method: 'DELETE' });
}

/* ---------------- Contact Form API ---------------- */

export function submitContactForm(data: { name: string; email: string; subject: string; message: string }): Promise<{ success: boolean }> {
    return apiFetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/* ---------------- Email Subscription API ---------------- */

export function subscribeEmail(email: string, source: string = 'landing'): Promise<{ success: boolean }> {
    return apiFetch('/api/subscribe', {
        method: 'POST',
        body: JSON.stringify({ email, source }),
    });
}

/* ---------------- Subscribers API (admin) ---------------- */

export interface Subscriber {
  id: number;
  email: string;
  source?: string;
  created_at?: string;
}

// Admin: all email subscribers. Routed through apiFetch so it carries the
// admin's bearer token (the endpoint is admin-only under D-002).
export function fetchSubscribers(): Promise<Subscriber[]> {
  return apiFetch('/api/subscribers');
}

/* ---------------- Teams Interest API ---------------- */

export function submitTeamsInterest(data: { companyName: string; adminEmail: string; teamSize: string; message: string }): Promise<{ success: boolean }> {
    return apiFetch('/api/teams-interest', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/* ---------------- Error Helpers ---------------- */

export function isApiError(error: any): error is Error {
  return error instanceof Error;
}

export function getApiErrorMessage(error: any): string {
  if (isApiError(error)) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

export function isConflictError(error: any): boolean {
  return isApiError(error) && error.message.includes('409');
}

export function isNotFoundError(error: any): boolean {
  return isApiError(error) && error.message.includes('404');
}

export function isUnauthorizedError(error: any): boolean {
  return isApiError(error) && (error.message.includes('401') || error.message.includes('403'));
}

/* ---------------- Helper Functions for Frontend Integration ---------------- */

// Helper to ensure user exists before creating card request
export async function ensureUserExists(userData: UserCreate): Promise<User> {
  try {
    // Try to find existing user by email
    return await fetchUserByEmail(userData.email);
  } catch (error) {
    // If user doesn't exist, create them
    if (isNotFoundError(error)) {
      return await syncUser(userData);
    }
    throw error;
  }
}

// Helper to create card request with user validation
export async function createCardRequestWithUser(
  userData: UserCreate,
  requestData: CardRequestCreate
): Promise<CardRequest> {
  try {
    // First ensure user exists
    await ensureUserExists(userData);

    // Then create the card request
    const result = await createCardRequest(requestData);
    return result;
  } catch (error) {
    throw error;
  }
}

/* ---------------- Valent signature generator ---------------- */

export interface ValentCopyEvent {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  fields: string | null; // JSON string of which optional fields were included
  created_at: string;
}

// Public: log a "Copy signature" press on /valent. Fire-and-forget — never
// block the user's copy if this fails.
export function logValentCopy(data: {
  name?: string;
  email?: string;
  phone?: string;
  fields?: Record<string, string>; // optional field name -> value the user entered
}): Promise<{ success: boolean }> {
  return apiFetch('/api/valent/copy', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Admin: all Valent copy events, newest first.
export function fetchValentCopies(): Promise<ValentCopyEvent[]> {
  return apiFetch('/api/valent/copies');
}